import { useState, useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

interface UseTypewriterOptions {
  /** The full text to type out. */
  text: string;
  /** Delay before typing starts, in ms. Default: 0 */
  delay?: number;
  /** Whether to start typing. Bind to isVisible from useIntersectionObserver. */
  enabled: boolean;
}

/**
 * Character-by-character typewriter effect.
 * Replaces initTypewriter() from app.js.
 * Returns [currentText, isTyping].
 */
export function useTypewriter(options: UseTypewriterOptions): [string, boolean] {
  const { text, delay = 0, enabled } = options;
  const prefersReduced = usePrefersReducedMotion();
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!enabled || hasStarted.current) return;

    if (prefersReduced) {
      setCurrentText(text);
      hasStarted.current = true;
      return;
    }

    hasStarted.current = true;
    setIsTyping(true);

    const delayTimeout = setTimeout(() => {
      let charIndex = 0;

      function typeChar() {
        if (charIndex < text.length) {
          setCurrentText(text.slice(0, charIndex + 1));
          charIndex++;
          setTimeout(typeChar, 25 + Math.random() * 20);
        } else {
          setIsTyping(false);
        }
      }

      typeChar();
    }, delay);

    return () => clearTimeout(delayTimeout);
  }, [enabled, text, delay, prefersReduced]);

  return [currentText, isTyping];
}
